import { createAttractionThemesConfig } from '@/config/objects/attraction-themes';
import { GenerateTranslationsDialog } from '@/components/shared/GenerateTranslationsDialog';
import { useGenerateTranslations } from '@/hooks/useGenerateTranslations';
import { Button, CrudFormView, PageContainer } from '@startupquickstart/frontend';
import { Languages } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Attraction theme form page for creating and editing themes
 */
export function AttractionThemeFormPage() {
  const { attractionId, themeId } = useParams<{ attractionId: string; themeId: string }>();
  const navigate = useNavigate();
  const [translationsDialogOpen, setTranslationsDialogOpen] = useState(false);
  const [savedTheme, setSavedTheme] = useState<{ id: string } | null>(null);
  const generateMutation = useGenerateTranslations();

  const themesConfig = useMemo(
    () => (attractionId ? createAttractionThemesConfig(attractionId) : null),
    [attractionId],
  );

  if (!attractionId || !themesConfig) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Invalid URL</h2>
          <p className="text-muted-foreground">Attraction ID is required</p>
        </div>
      </PageContainer>
    );
  }

  const isEditMode = themeId && themeId !== 'new';
  const itemId = isEditMode ? themeId : undefined;

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Attractions', href: '/attractions' },
    { label: 'Attraction', href: `/attractions/${attractionId}` },
    { label: 'Themes', href: `/attractions/${attractionId}` },
    { label: isEditMode ? 'Edit Theme' : 'New Theme', current: true },
  ];

  return (
    <PageContainer>
      <CrudFormView
        config={themesConfig}
        itemId={itemId}
        breadcrumbs={breadcrumbs}
        onSuccess={(item) => {
          if (isEditMode) {
            // If editing, redirect to detail page
            navigate(`/attractions/${attractionId}/themes/${item.id}`);
          } else {
            // If creating, show translation options
            setSavedTheme({
              id: item.id,
            });
          }
        }}
      />

      {savedTheme && !isEditMode && (
        <div className="mt-4 flex justify-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setTranslationsDialogOpen(true)}
          >
            <Languages className="h-4 w-4 mr-2" />
            Generate Translations
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={() => navigate(`/attractions/${attractionId}`)}
          >
            Done
          </Button>
        </div>
      )}

      {savedTheme && !isEditMode && (
        <GenerateTranslationsDialog
          open={translationsDialogOpen}
          onClose={() => {
            setTranslationsDialogOpen(false);
          }}
          title="Generate Translations"
          description="Select languages to translate this theme to. Existing translations will be skipped."
          onGenerate={(selectedLanguageIds) => {
            generateMutation.mutate(
              {
                attractionId: attractionId!,
                themeId: savedTheme.id,
                targetLanguageIds: selectedLanguageIds,
              },
              {
                onSuccess: () => {
                  setTranslationsDialogOpen(false);
                  // Navigate back after translations are generated
                  navigate(`/attractions/${attractionId}`);
                },
              },
            );
          }}
          isGenerating={generateMutation.isPending}
        />
      )}
    </PageContainer>
  );
}
